/**
 * Reading-progress bar.
 *
 * The bar is rendered inside the sticky masthead, pinned to its bottom edge —
 * see `.read-progress` in components.css for why it is not `position: fixed`
 * with a hardcoded top offset.
 *
 * It measures the ARTICLE BODY, not the document. Document scroll progress on an
 * article page is a lie: the comment form, the related-stories strip and the
 * footer are a third of the page's height, so the bar would read 66% at the end
 * of the text. What a reader wants to know is how much of the article is left.
 */
import { qs, prefersReducedMotion } from './dom.js';

export function setupProgress() {
  const bar = qs('[data-read-progress]');
  const article = qs('[data-article-body]');
  if (!bar || !article) return;

  // The bar animates a width on every scroll frame. Someone who asked for
  // reduced motion does not want that, and the bar is decorative — the article
  // is fully usable without it.
  if (prefersReducedMotion()) {
    bar.remove();
    return;
  }

  let queued = false;

  const update = () => {
    queued = false;
    const rect = article.getBoundingClientRect();
    // How far the article can scroll past the top of the viewport before its
    // end is reached. Non-positive means the article is shorter than the
    // viewport, so there is no progress to show.
    const scrollable = rect.height - window.innerHeight;

    if (scrollable <= 0) {
      bar.style.width = rect.bottom < 0 ? '100%' : '0';
      return;
    }

    const ratio = Math.min(1, Math.max(0, -rect.top / scrollable));
    bar.style.width = `${(ratio * 100).toFixed(1)}%`;
  };

  // rAF-throttled: a scroll listener that writes a style on every event forces
  // a layout per event, and on a long article that is hundreds per second.
  const onScroll = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();
}
